import { cva, VariantProps } from 'class-variance-authority';
import styles from './flex.module.css';

export const FLEX_BASIS_CSS_VALUES = {
  maxContent: 'max-content',
  minContent: 'min-content',
  fitContent: 'fit-content',
  content: 'content',
  inherit: 'inherit',
  initial: 'initial',
  revert: 'revert',
  revertLayer: 'revert-layer',
  unset: 'unset',
} as const;

export type FlexBasisToken = keyof typeof FLEX_BASIS_CSS_VALUES;

export const isFlexBasisToken = (value: unknown): value is FlexBasisToken => {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(FLEX_BASIS_CSS_VALUES, value)
  );
};

export const resolveFlexBasisCssValue = (
  value: FlexBasisToken | string | number,
) => {
  if (typeof value === 'number') {
    return value === 0 ? '0' : `${value}px`;
  }

  return isFlexBasisToken(value) ? FLEX_BASIS_CSS_VALUES[value] : value;
};

export const flexVariants = cva('', {
  variants: {
    display: {
      flex: styles['flex'],
      'inline-flex': styles['flex--inline'],
    },
    alignItems: {
      start: styles['flex-align-items--start'],
      flexStart: styles['flex-align-items--flex-start'],
      center: styles['flex-align-items--center'],
      flexEnd: styles['flex-align-items--flex-end'],
      end: styles['flex-align-items--end'],
      baseline: styles['flex-align-items--baseline'],
      stretch: styles['flex-align-items--stretch'],
      selfStart: styles['flex-align-items--self-start'],
      selfEnd: styles['flex-align-items--self-end'],
      selfCenter: styles['flex-align-items--self-center'],
    },
    justifyContent: {
      flexStart: styles['flex-justify-content--flex-start'],
      start: styles['flex-justify-content--start'],
      center: styles['flex-justify-content--center'],
      flexEnd: styles['flex-justify-content--flex-end'],
      end: styles['flex-justify-content--end'],
      spaceBetween: styles['flex-justify-content--space-between'],
      spaceAround: styles['flex-justify-content--space-around'],
      spaceEvenly: styles['flex-justify-content--space-evenly'],
      left: styles['flex-justify-content--left'],
      right: styles['flex-justify-content--right'],
      normal: styles['flex-justify-content--normal'],
      stretch: styles['flex-justify-content--stretch'],
      inherit: styles['flex-justify-content--inherit'],
      initial: styles['flex-justify-content--initial'],
      revert: styles['flex-justify-content--revert'],
      revertLayer: styles['flex-justify-content--revert-layer'],
      unset: styles['flex-justify-content--unset'],
    },
    flexWrap: {
      wrap: styles['flex-wrap--wrap'],
      nowrap: styles['flex-wrap--nowrap'],
      wrapReverse: styles['flex-wrap--wrap-reverse'],
    },
    flexGrow: {
      0: styles['flex-grow--0'],
      1: styles['flex-grow--1'],
      inherit: styles['flex-grow--inherit'],
      initial: styles['flex-grow--initial'],
      revert: styles['flex-grow--revert'],
      revertLayer: styles['flex-grow--revert-layer'],
      unset: styles['flex-grow--unset'],
    },
    flexShrink: {
      0: styles['flex-shrink--0'],
      1: styles['flex-shrink--1'],
      inherit: styles['flex-shrink--inherit'],
      initial: styles['flex-shrink--initial'],
      revert: styles['flex-shrink--revert'],
      revertLayer: styles['flex-shrink--revert-layer'],
      unset: styles['flex-shrink--unset'],
    },
    flexDirection: {
      row: styles['flex-direction--row'],
      rowReverse: styles['flex-direction--row-reverse'],
      column: styles['flex-direction--column'],
      columnReverse: styles['flex-direction--column-reverse'],
    },
    alignSelf: {
      auto: styles['flex-align-self--auto'],
      normal: styles['flex-align-self--normal'],
      start: styles['flex-align-self--start'],
      end: styles['flex-align-self--end'],
      center: styles['flex-align-self--center'],
      flexStart: styles['flex-align-self--flex-start'],
      flexEnd: styles['flex-align-self--flex-end'],
      selfStart: styles['flex-align-self--self-start'],
      selfEnd: styles['flex-align-self--self-end'],
      baseline: styles['flex-align-self--baseline'],
      stretch: styles['flex-align-self--stretch'],
      inherit: styles['flex-align-self--inherit'],
      initial: styles['flex-align-self--initial'],
      revert: styles['flex-align-self--revert'],
      revertLayer: styles['flex-align-self--revert-layer'],
      unset: styles['flex-align-self--unset'],
    },
    alignContent: {
      normal: styles['flex-align-content--normal'],
      start: styles['flex-align-content--start'],
      center: styles['flex-align-content--center'],
      end: styles['flex-align-content--end'],
      flexStart: styles['flex-align-content--flex-start'],
      flexEnd: styles['flex-align-content--flex-end'],
      baseline: styles['flex-align-content--baseline'],
      firstBaseline: styles['flex-align-content--first-baseline'],
      lastBaseline: styles['flex-align-content--last-baseline'],
      spaceBetween: styles['flex-align-content--space-between'],
      spaceAround: styles['flex-align-content--space-around'],
      spaceEvenly: styles['flex-align-content--space-evenly'],
      stretch: styles['flex-align-content--stretch'],
      safeCenter: styles['flex-align-content--safe-center'],
      unsafeCenter: styles['flex-align-content--unsafe-center'],
      inherit: styles['flex-align-content--inherit'],
      initial: styles['flex-align-content--initial'],
      revert: styles['flex-align-content--revert'],
      revertLayer: styles['flex-align-content--revert-layer'],
      unset: styles['flex-align-content--unset'],
    },
    flexBasis: {
      maxContent: styles['flex-basis--max-content'],
      minContent: styles['flex-basis--min-content'],
      fitContent: styles['flex-basis--fit-content'],
      content: styles['flex-basis--content'],
      inherit: styles['flex-basis--inherit'],
      initial: styles['flex-basis--initial'],
      revert: styles['flex-basis--revert'],
      revertLayer: styles['flex-basis--revert-layer'],
      unset: styles['flex-basis--unset'],
    },
    gap: {
      none: styles['flex-gap--none'],
      '5xs': styles['flex-gap--5xs'],
      '4xs': styles['flex-gap--4xs'],
      '3xs': styles['flex-gap--3xs'],
      '2xs': styles['flex-gap--2xs'],
      xs: styles['flex-gap--xs'],
      sm: styles['flex-gap--sm'],
      md: styles['flex-gap--md'],
      lg: styles['flex-gap--lg'],
      xl: styles['flex-gap--xl'],
      '2xl': styles['flex-gap--2xl'],
      '3xl': styles['flex-gap--3xl'],
      '4xl': styles['flex-gap--4xl'],
      '5xl': styles['flex-gap--5xl'],
      '6xl': styles['flex-gap--6xl'],
    },
    rowGap: {
      none: styles['flex-row-gap--none'],
      '5xs': styles['flex-row-gap--5xs'],
      '4xs': styles['flex-row-gap--4xs'],
      '3xs': styles['flex-row-gap--3xs'],
      '2xs': styles['flex-row-gap--2xs'],
      xs: styles['flex-row-gap--xs'],
      sm: styles['flex-row-gap--sm'],
      md: styles['flex-row-gap--md'],
      lg: styles['flex-row-gap--lg'],
      xl: styles['flex-row-gap--xl'],
      '2xl': styles['flex-row-gap--2xl'],
      '3xl': styles['flex-row-gap--3xl'],
      '4xl': styles['flex-row-gap--4xl'],
      '5xl': styles['flex-row-gap--5xl'],
      '6xl': styles['flex-row-gap--6xl'],
    },
    columnGap: {
      none: styles['flex-column-gap--none'],
      '5xs': styles['flex-column-gap--5xs'],
      '4xs': styles['flex-column-gap--4xs'],
      '3xs': styles['flex-column-gap--3xs'],
      '2xs': styles['flex-column-gap--2xs'],
      xs: styles['flex-column-gap--xs'],
      sm: styles['flex-column-gap--sm'],
      md: styles['flex-column-gap--md'],
      lg: styles['flex-column-gap--lg'],
      xl: styles['flex-column-gap--xl'],
      '2xl': styles['flex-column-gap--2xl'],
      '3xl': styles['flex-column-gap--3xl'],
      '4xl': styles['flex-column-gap--4xl'],
      '5xl': styles['flex-column-gap--5xl'],
      '6xl': styles['flex-column-gap--6xl'],
    },
  },
  defaultVariants: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flexStart',
  },
});

export type FlexVariants = VariantProps<typeof flexVariants>;

export const flexArgTypes = {
  display: {
    control: 'select',
    options: ['flex', 'inline-flex'],
    description: 'CSS display property for flex container',
    table: {
      type: { summary: 'flex | inline-flex' },
      category: 'Layout',
    },
  },
  flexDirection: {
    control: 'select',
    options: ['row', 'row-reverse', 'column', 'column-reverse'],
    description: '**[Responsive]** Defines the direction of flex items',
    table: {
      type: { summary: 'row | row-reverse | column | column-reverse' },
      category: 'Layout',
    },
  },
  flexWrap: {
    control: 'select',
    options: ['wrap', 'nowrap', 'wrapReverse'],
    description: 'Controls whether flex items wrap onto multiple lines',
    table: {
      type: { summary: 'wrap | nowrap | wrapReverse' },
      category: 'Layout',
    },
  },
  justifyContent: {
    control: 'select',
    options: [
      'flexStart',
      'start',
      'center',
      'flexEnd',
      'end',
      'spaceBetween',
      'spaceAround',
      'spaceEvenly',
      'left',
      'right',
      'normal',
      'stretch',
      'inherit',
      'initial',
      'revert',
      'revertLayer',
      'unset',
    ],
    description: '**[Responsive]** Aligns flex items along the main axis',
    table: {
      type: { summary: 'JustifyContent' },
      category: 'Layout',
    },
  },
  alignItems: {
    control: 'select',
    options: [
      'start',
      'flexStart',
      'center',
      'flexEnd',
      'end',
      'baseline',
      'stretch',
      'selfStart',
      'selfEnd',
      'selfCenter',
    ],
    description: '**[Responsive]** Aligns flex items along the cross axis',
    table: {
      type: { summary: 'AlignItems' },
      category: 'Layout',
    },
  },
  alignContent: {
    control: 'select',
    options: [
      'normal',
      'start',
      'center',
      'end',
      'flexStart',
      'flexEnd',
      'baseline',
      'firstBaseline',
      'lastBaseline',
      'spaceBetween',
      'spaceAround',
      'spaceEvenly',
      'stretch',
      'safeCenter',
      'unsafeCenter',
      'inherit',
      'initial',
      'revert',
      'revertLayer',
      'unset',
    ],
    description:
      'Aligns flex lines when there is extra space in the cross axis',
    table: {
      type: { summary: 'AlignContent' },
      category: 'Layout',
    },
  },
  alignSelf: {
    control: 'select',
    options: [
      'auto',
      'normal',
      'start',
      'end',
      'center',
      'flexStart',
      'flexEnd',
      'selfStart',
      'selfEnd',
      'baseline',
      'stretch',
      'inherit',
      'initial',
      'revert',
      'revertLayer',
      'unset',
    ],
    description: 'Overrides align-items for individual flex items',
    table: {
      type: { summary: 'AlignSelf' },
      category: 'Layout',
    },
  },
  gap: {
    control: 'select',
    options: [
      'none',
      '5xs',
      '4xs',
      '3xs',
      '2xs',
      'xs',
      'sm',
      'md',
      'lg',
      'xl',
      '2xl',
      '3xl',
      '4xl',
      '5xl',
      '6xl',
    ],
    description:
      '**[Responsive]** Gap between flex items (both row and column)',
    table: {
      type: { summary: 'GapSize' },
      category: 'Layout',
    },
  },
  rowGap: {
    control: 'select',
    options: [
      'none',
      '5xs',
      '4xs',
      '3xs',
      '2xs',
      'xs',
      'sm',
      'md',
      'lg',
      'xl',
      '2xl',
      '3xl',
      '4xl',
      '5xl',
      '6xl',
    ],
    description: '**[Responsive]** Gap between flex rows',
    table: {
      type: { summary: 'GapSize' },
      category: 'Layout',
    },
  },
  columnGap: {
    control: 'select',
    options: [
      'none',
      '5xs',
      '4xs',
      '3xs',
      '2xs',
      'xs',
      'sm',
      'md',
      'lg',
      'xl',
      '2xl',
      '3xl',
      '4xl',
      '5xl',
      '6xl',
    ],
    description: '**[Responsive]** Gap between flex columns',
    table: {
      type: { summary: 'GapSize' },
      category: 'Layout',
    },
  },
  flexGrow: {
    control: 'select',
    options: [0, 1, 'inherit', 'initial', 'revert', 'revertLayer', 'unset'],
    description: 'Defines the ability for a flex item to grow',
    table: {
      type: {
        summary: '0 | 1 | inherit | initial | revert | revertLayer | unset',
      },
      category: 'Layout',
    },
  },
  flexShrink: {
    control: 'select',
    options: [0, 1, 'inherit', 'initial', 'revert', 'revertLayer', 'unset'],
    description: 'Defines the ability for a flex item to shrink',
    table: {
      type: {
        summary: '0 | 1 | inherit | initial | revert | revertLayer | unset',
      },
      category: 'Layout',
    },
  },
  flexBasis: {
    control: 'select',
    options: [
      'maxContent',
      'minContent',
      'fitContent',
      'content',
      'inherit',
      'initial',
      'revert',
      'revertLayer',
      'unset',
    ],
    description:
      '**[Responsive]** Defines the default size of a flex item before remaining space is distributed. Accepts predefined flex-basis tokens and arbitrary CSS values (e.g. "200px", "30%", "clamp(200px, 50vw, 400px)").',
    table: {
      type: { summary: 'FlexBasis' },
      category: 'Layout',
    },
  },
};
